import React, { memo, useEffect, useState } from "react";
import {
  BarChart3,
  BatteryCharging,
  Building2,
  ChevronDown,
  DownloadCloud,
  FileText,
  Layers3,
  LoaderCircle,
  LogOut,
  Megaphone,
  Menu,
  MonitorCog,
  PlugZap,
  Settings,
  ShieldAlert,
  User,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import logo from "../../assests/ufc_logo_mark.png";
import { useConfig } from "../../context/configContext";
import MapViewButton from "../MapViewButton";
import api from "../interceptors.js";
import "./index.css";

const NavItem = memo(({ to, icon, label, collapsed, onClick, isActive }) => (
  <NavLink
    to={to}
    title={collapsed ? label : undefined}
    onClick={onClick}
    className={({ isActive: active }) =>
      `nav-link ${collapsed ? "is-collapsed" : ""} ${
        active || isActive ? "is-active" : ""
      }`
    }
  >
    <span className="nav-icon">{icon}</span>
    {!collapsed && <span className="nav-label">{label}</span>}
  </NavLink>
));

NavItem.displayName = "NavItem";

const SubItem = memo(({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) => `nav-sub-link ${isActive ? "is-active" : ""}`}
  >
    {label}
  </NavLink>
));

SubItem.displayName = "SubItem";

const Dropdown = memo(
  ({ label, icon, open, setOpen, collapsed, children }) => (
    <div className="nav-dropdown">
      <button
        type="button"
        title={collapsed ? label : undefined}
        onClick={() => setOpen(!open)}
        className={`nav-link nav-button ${collapsed ? "is-collapsed" : ""} ${
          open ? "is-open" : ""
        }`}
      >
        <span className="nav-icon">{icon}</span>
        {!collapsed && (
          <>
            <span className="nav-label">{label}</span>
            <ChevronDown size={16} className="nav-chevron" />
          </>
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && !collapsed && (
          <motion.div
            key="dropdown-content"
            initial={{ opacity: 0, height: 0, y: -4 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="nav-sub-group"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  ),
);

Dropdown.displayName = "Dropdown";

const Navigation = () => {
  const { config, resetConfig } = useConfig();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const dispensers = config.dispensers ?? 0;
  const stationId = config.station_id;
  const dispensorData = config.dispensersData ?? [];
  const isMapView = dispensers <= 0 || pathname.startsWith("/maps");

  const sortedDispensors = [...dispensorData].sort(
    (a, b) => Number(a.dispensor_id.slice(1)) - Number(b.dispensor_id.slice(1)),
  );

  const sortedDispensorsWithGuns = sortedDispensors.map((dispensor) => ({
    ...dispensor,
    guns: [...(dispensor.guns || [])].sort(
      (a, b) =>
        Number(a.gun_id.replace("GUN", "")) -
        Number(b.gun_id.replace("GUN", "")),
    ),
  }));

  const [openDisp, setOpenDisp] = useState(false);
  const [openGraph, setOpenGraph] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigationCollapsed = false;

  useEffect(() => {
    const resize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const logout = async () => {
    setLoggingOut(true);

    try {
      await api.post("/api/logout", {});
    } catch (err) {
      console.error("Logout failed", err);
    } finally {
      resetConfig();
      localStorage.clear();
      navigate("/login", { replace: true });
      setLoggingOut(false);
    }
  };

  return (
    <div className="app-shell">
      {!mobileOpen && (
        <button
          type="button"
          className="mobile-menu-button"
          aria-label="Open navigation"
          onClick={() => setMobileOpen(true)}
        >
          <Menu size={22} />
        </button>
      )}

      <aside
        className={`app-sidebar ${navigationCollapsed ? "is-collapsed" : ""} ${
          mobileOpen ? "is-mobile-open" : ""
        }`}
      >
        <div className="sidebar-top">
          <div className="sidebar-brand-row">
            <NavLink to="/" className="brand-lockup" onClick={closeMobile}>
              <span className="brand-mark">
                <img src={logo} alt="Ultrafast Chargers" />
              </span>
              {!navigationCollapsed && (
                <span className="brand-copy">
                  <strong>Ultrafast</strong>
                  <small>Chargers</small>
                </span>
              )}
            </NavLink>
          </div>

          {!navigationCollapsed && (
            <div className="sidebar-context-card">
              <span>{isMapView ? "Map mode" : "Active station"}</span>
              <strong>{config.station_name || "Ultrafast network"}</strong>
              {stationId && <small>Station {stationId}</small>}
            </div>
          )}

          {!isMapView && (
            <nav className="nav-group" aria-label="Main navigation">
              <NavItem
                to="/"
                icon={<Layers3 size={19} />}
                label="Dashboard"
                collapsed={navigationCollapsed}
                onClick={closeMobile}
              />

              <Dropdown
                label="Dispensers"
                icon={<BatteryCharging size={19} />}
                open={openDisp}
                setOpen={setOpenDisp}
                collapsed={navigationCollapsed}
              >
                {sortedDispensors.length === 0 ? (
                  <p className="nav-empty">No dispensers found</p>
                ) : (
                  sortedDispensors.map((dispensor) => (
                    <SubItem
                      key={dispensor.dispensor_id}
                      to={`/${stationId}/${dispensor.dispensor_id}`}
                      label={dispensor.dispensor_id}
                      onClick={closeMobile}
                    />
                  ))
                )}
              </Dropdown>

              <Dropdown
                label="Logs"
                icon={<FileText size={19} />}
                open={openLogs}
                setOpen={setOpenLogs}
                collapsed={navigationCollapsed}
              >
                <SubItem to="/general" label="General" onClick={closeMobile} />

                {dispensorData.length === 0 ? (
                  <p className="nav-empty">No logs available</p>
                ) : (
                  sortedDispensorsWithGuns.map((dispensor) =>
                    dispensor.guns?.map((gun) => (
                      <SubItem
                        key={gun.gun_id}
                        to={`/logs/${gun.gun_id}`}
                        label={gun.gun_id}
                        onClick={closeMobile}
                      />
                    )),
                  )
                )}
              </Dropdown>

              <Dropdown
                label="Graph view"
                icon={<BarChart3 size={19} />}
                open={openGraph}
                setOpen={setOpenGraph}
                collapsed={navigationCollapsed}
              >
                {sortedDispensors.length === 0 ? (
                  <p className="nav-empty">No dispensers found</p>
                ) : (
                  sortedDispensors.map((dispensor) => (
                    <SubItem
                      key={dispensor.dispensor_id}
                      to={`/graphView/${stationId}/${dispensor.dispensor_id}`}
                      label={dispensor.dispensor_id}
                      onClick={closeMobile}
                    />
                  ))
                )}
              </Dropdown>

              <NavItem
                to="/ChargerProfile"
                icon={<PlugZap size={19} />}
                label="Charger Profile"
                collapsed={navigationCollapsed}
                onClick={closeMobile}
              />

              <NavItem
                to="/chargerUiUpdate"
                icon={<DownloadCloud size={19} />}
                label="Charger Updates"
                collapsed={navigationCollapsed}
                onClick={closeMobile}
              />

              <NavItem
                to="/advertisers"
                icon={<Building2 size={19} />}
                label="Advertisers"
                collapsed={navigationCollapsed}
                onClick={closeMobile}
              />

              <NavItem
                to="/campaigns"
                icon={<Megaphone size={19} />}
                label="Campaigns"
                collapsed={navigationCollapsed}
                onClick={closeMobile}
              />
            </nav>
          )}
        </div>

        <div className="sidebar-bottom">
          <MapViewButton collapsed={false} onClick={closeMobile} />

          <NavItem
            to="/device-setup"
            icon={<MonitorCog size={19} />}
            label="Device Setup"
            collapsed={false}
            onClick={closeMobile}
          />
          <NavItem
            to="/config"
            icon={<Settings size={19} />}
            label="Settings"
            collapsed={false}
            onClick={closeMobile}
          />
          <NavItem
            to="/profile"
            icon={<User size={19} />}
            label="Profile"
            collapsed={false}
            onClick={closeMobile}
          />

          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              setShowLogoutModal(true);
            }}
            disabled={loggingOut}
            className="logout-button"
          >
            {loggingOut ? (
              <LoaderCircle size={18} className="animate-spin" />
            ) : (
              <LogOut size={18} />
            )}
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="mobile-backdrop"
          aria-label="Close navigation"
          onClick={closeMobile}
        />
      )}

      <main className="app-main-surface">
        <Outlet />
      </main>

      <nav className="quick-tab-bar" aria-label="Quick actions">
        <MapViewButton collapsed={false} onClick={closeMobile} />

        <NavItem
          to="/device-setup"
          icon={<MonitorCog size={19} />}
          label="Device Setup"
          collapsed={false}
          onClick={closeMobile}
        />
        <NavItem
          to="/config"
          icon={<Settings size={19} />}
          label="Settings"
          collapsed={false}
          onClick={closeMobile}
        />
        <NavItem
          to="/profile"
          icon={<User size={19} />}
          label="Profile"
          collapsed={false}
          onClick={closeMobile}
        />

        <button
          type="button"
          onClick={() => {
            setMobileOpen(false);
            setShowLogoutModal(true);
          }}
          disabled={loggingOut}
          className="logout-button"
        >
          {loggingOut ? (
            <LoaderCircle size={18} className="animate-spin" />
          ) : (
            <LogOut size={18} />
          )}
          <span>Logout</span>
        </button>
      </nav>

      {showLogoutModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="logout-modal"
          >
            <button
              type="button"
              className="modal-close-button"
              aria-label="Close logout dialog"
              onClick={() => setShowLogoutModal(false)}
            >
              <X size={18} />
            </button>

            <div className="logout-modal-visual">
              <span className="logout-modal-icon">
                <ShieldAlert size={30} />
              </span>
              <span className="logout-modal-pulse" />
            </div>

            <div className="logout-modal-copy">
              <p>Session control</p>
              <h2>End this dashboard session?</h2>
              <span>
                You will be returned to login and live station controls will be
                locked on this browser.
              </span>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="modal-secondary"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-danger"
                onClick={() => {
                  setShowLogoutModal(false);
                  logout();
                }}
                disabled={loggingOut}
              >
                {loggingOut && (
                  <LoaderCircle size={17} className="animate-spin" />
                )}
                End session
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Navigation;
